import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  ShieldCheck,
} from '@saleshandy/icons';
import toaster, { Theme } from '../../../../../../shared/toaster';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import {
  CHROME_EXTENSIONS_URL,
  INSTALL_STEPS,
  LEAD_FINDER_EXTENSION_ZIP_NAME,
  LEAD_FINDER_EXTENSION_ZIP_URL,
} from './constants';
import {
  ChromeExtensionsIllustration,
  DownloadIllustration,
  LoadUnpackedIllustration,
  UnzipIllustration,
} from './install-illustrations';

// One illustration per install step (index-aligned with INSTALL_STEPS).
const VISUALS = [
  <DownloadIllustration />,
  <UnzipIllustration />,
  <ChromeExtensionsIllustration />,
  <LoadUnpackedIllustration />,
];

const AUTO_ADVANCE_MS = 3000;
// How long auto-advance stays paused after the user manually picks a step.
const MANUAL_PAUSE_MS = 12000;

// Chrome blocks web pages from navigating to chrome:// URLs, so we copy the
// link to the clipboard for the user to paste into their address bar instead.
const copyChromeUrl = () => {
  navigator.clipboard
    ?.writeText(CHROME_EXTENSIONS_URL)
    .then(() =>
      toaster.success(
        "Copied 'chrome://extensions' — paste it into your address bar.",
        { theme: Theme.New },
      ),
    )
    .catch(() =>
      toaster.info('Open a new tab and go to chrome://extensions', {
        theme: Theme.New,
      }),
    );
};

const InstallStep: React.FC = () => {
  const total = INSTALL_STEPS.length;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const pauseTimer = useRef<ReturnType<typeof setTimeout>>();

  // Auto-advance the carousel unless paused by a manual interaction.
  useEffect(() => {
    if (paused) {
      return undefined;
    }
    const id = setTimeout(
      () => setActive((a) => (a + 1) % total),
      AUTO_ADVANCE_MS,
    );
    return () => clearTimeout(id);
  }, [active, paused, total]);

  // Clean up the pause timer on unmount.
  useEffect(
    () => () => {
      if (pauseTimer.current) {
        clearTimeout(pauseTimer.current);
      }
    },
    [],
  );

  const selectStep = (idx: number) => {
    setActive(idx);
    setPaused(true);
    if (pauseTimer.current) {
      clearTimeout(pauseTimer.current);
    }
    pauseTimer.current = setTimeout(() => setPaused(false), MANUAL_PAUSE_MS);
  };

  const renderStepAction = (action?: string) => {
    if (action === 'download') {
      return (
        <a
          className="bs-lf-ext__step-action"
          href={LEAD_FINDER_EXTENSION_ZIP_URL}
          download={LEAD_FINDER_EXTENSION_ZIP_NAME}
          onClick={(e) => e.stopPropagation()}
        >
          <Download width={14} height={14} />
          Download
        </a>
      );
    }
    if (action === 'open-chrome') {
      return (
        <button
          type="button"
          className="bs-lf-ext__step-action"
          onClick={(e) => {
            e.stopPropagation();
            copyChromeUrl();
          }}
        >
          <Copy width={14} height={14} />
          Copy link
        </button>
      );
    }
    return null;
  };

  return (
    <div className="bs-lf-ext__install">
      <h4 className="bs-lf-ext__install-title semibold-3 gray-txt-9">
        Quick installation
      </h4>
      <p className="bs-lf-ext__install-subtitle regular-1 gray-txt-11">
        Takes about 3 minutes · click any step to jump ahead
      </p>

      <div className="bs-lf-ext__carousel">
        <div className="bs-lf-ext__carousel-viewport">
          <div
            className="bs-lf-ext__carousel-track"
            style={{ transform: `translateX(-${active * 100}%)` }}
          >
            {VISUALS.map((visual, i) => (
              <div
                key={INSTALL_STEPS[i].title}
                className="bs-lf-ext__carousel-slide"
              >
                {visual}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="bs-lf-ext__carousel-arrow bs-lf-ext__carousel-arrow--prev"
            aria-label="Previous step"
            onClick={() => selectStep((active - 1 + total) % total)}
          >
            <ChevronLeft width={14} height={14} />
          </button>
          <button
            type="button"
            className="bs-lf-ext__carousel-arrow bs-lf-ext__carousel-arrow--next"
            aria-label="Next step"
            onClick={() => selectStep((active + 1) % total)}
          >
            <ChevronRight width={14} height={14} />
          </button>

          <div className="bs-lf-ext__carousel-dots">
            {INSTALL_STEPS.map((item, i) => (
              <button
                key={item.title}
                type="button"
                aria-label={`Go to step ${i + 1}`}
                className={classNames('bs-lf-ext__carousel-dot', {
                  'bs-lf-ext__carousel-dot--active': i === active,
                })}
                onClick={() => selectStep(i)}
              />
            ))}
          </div>
        </div>
      </div>

      <ul className="bs-lf-ext__checklist">
        {INSTALL_STEPS.map((item, idx) => {
          const isCompleted = active > idx;
          const isActive = active === idx;

          return (
            <li
              key={item.title}
              className={classNames('bs-lf-ext__checklist-row', {
                'bs-lf-ext__checklist-row--active': isActive,
              })}
              {...accessibleOnClick(() => selectStep(idx))}
            >
              <span
                className={classNames('bs-lf-ext__checklist-dot', {
                  'bs-lf-ext__checklist-dot--active': isActive,
                  'bs-lf-ext__checklist-dot--completed': isCompleted,
                })}
              >
                {isCompleted ? (
                  <Check width={14} height={14} className="white-txt-1" />
                ) : (
                  idx + 1
                )}
              </span>
              <div className="bs-lf-ext__checklist-text">
                <p className="semibold-1 gray-txt-9 mb-0">{item.title}</p>
                <p className="regular-1 gray-txt-11 mb-0">{item.description}</p>
              </div>
              {renderStepAction(item.action)}
            </li>
          );
        })}
      </ul>

      <div className="bs-lf-ext__safety">
        <ShieldCheck width={16} height={16} className="blue-txt-6" />
        <p className="regular-1 gray-txt-11 mb-0">
          <span className="semibold-1 gray-txt-9">
            Installing this way is completely safe.
          </span>{' '}
          It&apos;s just how we deliver new capabilities and features faster to
          you, straight from Saleshandy, not the Chrome Web Store.
        </p>
      </div>
    </div>
  );
};

export default InstallStep;
